import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Folder, Plus, Edit, Trash2, FolderOpen } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  getStores,
  createStore,
  updateStore,
  deleteStore,
  FileSearchStore,
} from '../../services/FileSearchStores';

const FileSearchStoresPage: React.FC = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState<FileSearchStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<FileSearchStore | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storeToDelete, setStoreToDelete] = useState<FileSearchStore | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    setLoading(true);
    try {
      const data = await getStores();
      setStores(data);
    } catch (error) {
      toast.error('Error loading stores');
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setEditingStore(null);
    setFormData({ name: '', description: '' });
    setDialogOpen(true);
  };

  const handleEditClick = (store: FileSearchStore) => {
    setEditingStore(store);
    setFormData({ name: store.name, description: store.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Store name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingStore) {
        await updateStore(editingStore.id, formData);
        toast.success(`Store "${formData.name}" updated successfully`);
      } else {
        await createStore(formData);
        toast.success(`Store "${formData.name}" created successfully`);
      }
      setDialogOpen(false);
      fetchStores();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error?.message || 'Error saving store';
      toast.error(errorMessage);
      console.error('Error saving store:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (store: FileSearchStore) => {
    setStoreToDelete(store);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!storeToDelete) return;

    setDeleting(true);
    try {
      await deleteStore(storeToDelete.id);
      toast.success(`Store "${storeToDelete.name}" deleted successfully`);
      fetchStores();
    } catch (error) {
      toast.error('Error deleting store');
      console.error('Error deleting store:', error);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setStoreToDelete(null);
    }
  };

  const handleStoreClick = (storeId: string) => {
    navigate(`/file-search-stores/${storeId}`);
  };

  const filteredStores = stores.filter((store) =>
    store.name.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <div className="w-full space-y-4 p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">File Search Stores</h1>
          <p className="text-muted-foreground">
            Manage document namespaces for AI-powered search
          </p>
        </div>
        <Button onClick={handleCreateClick}>
          <Plus className="mr-2 h-4 w-4" />
          Create Store
        </Button>
      </div>

      <div className="flex items-center py-4">
        <Input
          placeholder="Filter stores..."
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          className="max-w-sm"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading stores...</p>
        </div>
      ) : filteredStores.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <FolderOpen className="h-16 w-16 text-muted-foreground" />
          <div className="text-center">
            <h3 className="text-lg font-medium">No stores found</h3>
            <p className="text-muted-foreground">
              {filterText ? 'Try adjusting your search' : 'Create a store to get started'}
            </p>
          </div>
          {!filterText && (
            <Button onClick={handleCreateClick}>
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Store
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredStores.map((store) => (
            <Card key={store.id} className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader onClick={() => handleStoreClick(store.id)}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Folder className="h-5 w-5 text-primary" />
                    <CardTitle className="text-xl">{store.name}</CardTitle>
                  </div>
                </div>
                {store.description && (
                  <CardDescription className="mt-2">
                    {store.description}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent onClick={() => handleStoreClick(store.id)}>
                <p className="text-sm text-muted-foreground">
                  {store.documentCount || 0} document{store.documentCount !== 1 ? 's' : ''}
                </p>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(store);
                  }}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(store);
                  }}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingStore ? 'Edit Store' : 'Create Store'}</DialogTitle>
            <DialogDescription>
              {editingStore
                ? 'Update store details'
                : 'Create a new file search store to organize your documents'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter store name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter store description (optional)"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingStore ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Store</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{storeToDelete?.name}"? This action cannot be
              undone and will remove all documents in this store from Gemini File Search.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FileSearchStoresPage;
